import { Position, PositionRef, PositionRoleRef } from "services/positions.service";

/**
 * Pure helpers of the positions catalogue UI. Order matters everywhere: an
 * employee's positions are printed on the contract joined by ", " in the order
 * they were chosen (docs/prd/positions-catalogue.md §1 rule 4), so every
 * selection is an ordered id array, never a set.
 */

/** Adds `id` at the end, or removes it when already chosen. */
export function togglePositionId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id];
}

/** Moves the chip at `index` by `delta` places; out-of-range moves are no-ops. */
export function movePositionId(ids: string[], index: number, delta: number): string[] {
  const target = index + delta;
  if (index < 0 || index >= ids.length || target < 0 || target >= ids.length) {
    return ids;
  }
  const next = [...ids];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** The label for the UI language; EN falls back to LT (EN is optional). */
export function positionName(
  position: Pick<PositionRef, "nameLT" | "nameEN"> | null | undefined,
  lang: string
): string {
  if (!position) return "";
  if (lang === "en" && position.nameEN && position.nameEN.trim()) return position.nameEN;
  return position.nameLT || "";
}

/** Names of the chosen ids, in chosen order, joined like the contract prints them. */
export function joinPositionNames(
  ids: string[],
  byId: Map<string, Pick<PositionRef, "nameLT" | "nameEN">>,
  lang: string
): string {
  return ids
    .map((id) => positionName(byId.get(id), lang))
    .filter(Boolean)
    .join(", ");
}

/** Case-insensitive match on any of the names (LT, feminine LT, EN). */
export function filterPositions<T extends Pick<PositionRef, "nameLT" | "nameEN" | "nameLTFemale">>(
  positions: T[],
  query: string
): T[] {
  const needle = query.trim().toLocaleLowerCase("lt");
  if (!needle) return positions;
  return positions.filter(
    (position) =>
      (position.nameLT || "").toLocaleLowerCase("lt").includes(needle) ||
      (position.nameLTFemale || "").toLocaleLowerCase("lt").includes(needle) ||
      (position.nameEN || "").toLocaleLowerCase("lt").includes(needle)
  );
}

/** «Buhalteris / Buhalterė» — the LT title with its feminine form, when there is one. */
export function genderedTitle(position: Pick<PositionRef, "nameLT" | "nameLTFemale">): string {
  const female = (position.nameLTFemale || "").trim();
  return female && female !== position.nameLT ? `${position.nameLT} / ${female}` : position.nameLT;
}

/**
 * Merge dialog default for «use the source as the feminine title»: only when
 * the target has none yet, and on by default when the source looks feminine
 * (ends with «ė», e.g. «Buhalterė»).
 */
export function femaleNameMergeOption(
  source: Pick<PositionRef, "nameLT">,
  target: Pick<PositionRef, "nameLTFemale"> | null
): { available: boolean; defaultOn: boolean } {
  const available = !!target && !(target.nameLTFemale || "").trim();
  return { available, defaultOn: available && /ė$/i.test((source.nameLT || "").trim()) };
}

/** True when both arrays hold the same ids in the same order (print order matters). */
export function sameIdOrder(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

/** True when both arrays hold the same ids, ignoring order. */
export function sameIdSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

export interface RoleAreaGroup {
  areaId: string;
  areaName: string;
  roles: PositionRoleRef[];
}

/**
 * Chips grouped by sphere, spheres and roles sorted by name. Roles without a
 * sphere (pre-areas data) form one group with an empty id.
 */
export function groupRolesByArea(roles: PositionRoleRef[]): RoleAreaGroup[] {
  const groups = new Map<string, RoleAreaGroup>();
  roles.forEach((role) => {
    const key = role.areaId || "";
    if (!groups.has(key)) {
      groups.set(key, { areaId: key, areaName: role.areaName || "", roles: [] });
    }
    groups.get(key)!.roles.push(role);
  });
  const sorted = Array.from(groups.values()).sort((a, b) =>
    a.areaName.localeCompare(b.areaName, "lt")
  );
  sorted.forEach((group) => group.roles.sort((a, b) => a.name.localeCompare(b.name, "lt")));
  return sorted;
}

/**
 * What a merge leaves on the target: the union of both positions'
 * pareigybės (§3 mergePositions), deduplicated by id, target's first.
 */
export function unionRoleRefs(
  target: PositionRoleRef[],
  source: PositionRoleRef[]
): PositionRoleRef[] {
  const seen = new Set<string>();
  const out: PositionRoleRef[] = [];
  [...target, ...source].forEach((role) => {
    if (seen.has(role.id)) return;
    seen.add(role.id);
    out.push(role);
  });
  return out;
}

/**
 * The full role-id list to PUT when linking (or unlinking) one pareigybė on
 * a position — the endpoint replaces the set, so the rest must be sent back.
 */
export function positionRoleIdsWith(position: Pick<Position, "roles">, roleId: string, linked: boolean): string[] {
  const current = position.roles.map((role) => role.id);
  if (linked) return current.includes(roleId) ? current : [...current, roleId];
  return current.filter((id) => id !== roleId);
}

/**
 * The role ids to PUT from the edit dialog: the checked roles of the spheres
 * the user manages plus, untouched, every linked role of a sphere they do not
 * (the backend keeps those anyway; sending them keeps the request honest).
 */
export function mergeEditableRoleIds(
  linked: PositionRoleRef[],
  managedAreaIds: Set<string>,
  checkedManagedRoleIds: string[]
): string[] {
  const readOnly = linked
    .filter((role) => !role.areaId || !managedAreaIds.has(role.areaId))
    .map((role) => role.id);
  return Array.from(new Set([...readOnly, ...checkedManagedRoleIds]));
}

/** Minimal shape `groupRolesByGroup` needs off a work-security role. */
type GroupableRole = {
  id: string;
  name: string;
  group?: { id: string; name: string; sortOrder?: number } | null;
};

export interface RoleGroupBucket<T> {
  /** "" = the ungrouped bucket. */
  groupId: string;
  /** "" for the ungrouped bucket — the caller supplies its own label. */
  groupName: string;
  roles: T[];
}

/**
 * Roles of one sphere grouped by `role.group` for the pareigybės checklist
 * (PositionEditDialog, IndividualRoleAdd): groups ordered by `sortOrder` when
 * every group carries one, else by name ('lt'); roles within a group by name
 * ('lt'); the ungrouped bucket (groupId "") always comes last, including when
 * it is the only bucket — that "no groups at all" case is what lets the
 * caller fall back to a flat list with no group headers.
 */
export function groupRolesByGroup<T extends GroupableRole>(roles: T[]): RoleGroupBucket<T>[] {
  type Bucket = RoleGroupBucket<T> & { sortOrder?: number };
  const buckets = new Map<string, Bucket>();
  roles.forEach((role) => {
    const key = role.group?.id || "";
    if (!buckets.has(key)) {
      buckets.set(key, {
        groupId: key,
        groupName: role.group?.name || "",
        roles: [],
        sortOrder: role.group?.sortOrder,
      });
    }
    buckets.get(key)!.roles.push(role);
  });
  const sorted = Array.from(buckets.values()).sort((a, b) => {
    if (!a.groupId && b.groupId) return 1;
    if (a.groupId && !b.groupId) return -1;
    if (a.sortOrder !== undefined && b.sortOrder !== undefined && a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder;
    }
    return a.groupName.localeCompare(b.groupName, "lt");
  });
  sorted.forEach((bucket) => bucket.roles.sort((a, b) => a.name.localeCompare(b.name, "lt")));
  return sorted.map(({ groupId, groupName, roles: bucketRoles }) => ({ groupId, groupName, roles: bucketRoles }));
}

export type GroupCheckState = "none" | "some" | "all";

/** Tri-state of a group's "select all" checkbox: none / some / all of `groupRoleIds` are in `checkedRoleIds`. */
export function groupCheckState(groupRoleIds: string[], checkedRoleIds: string[]): GroupCheckState {
  if (groupRoleIds.length === 0) return "none";
  const checked = new Set(checkedRoleIds);
  const count = groupRoleIds.filter((id) => checked.has(id)).length;
  if (count === 0) return "none";
  return count === groupRoleIds.length ? "all" : "some";
}

/** Checks (or unchecks) every id of one group inside a larger checked-id list, leaving the rest untouched. */
export function setGroupRolesChecked(
  checkedRoleIds: string[],
  groupRoleIds: string[],
  checked: boolean
): string[] {
  const groupSet = new Set(groupRoleIds);
  const kept = checkedRoleIds.filter((id) => !groupSet.has(id));
  return checked ? [...kept, ...groupRoleIds] : kept;
}

/** What the last automatic prefill put into the positions field, and for whom. */
export interface PositionsPrefillMark {
  userId: string;
  ids: string[];
}

/**
 * Contract registration: choosing the employee prefills their current
 * positions. Decides, against the form's LATEST data, what an answer for
 * `userId` may do:
 *  - the employee is no longer the selected one → nothing (stale answer);
 *  - the field holds a manual pick → nothing, ever;
 *  - the field is empty, or still exactly the previous prefill → replace it
 *    and remember this prefill.
 * Returns the fields to merge into the form data, or null for "leave as is".
 */
export function decidePositionsPrefill(
  form: { user?: string | null; positionIds?: string[] | null; positionsPrefill?: PositionsPrefillMark | null },
  userId: string,
  ids: string[]
): { positionIds: string[]; positionsPrefill: PositionsPrefillMark } | null {
  if (!userId || form.user !== userId) return null;
  const shown = Array.isArray(form.positionIds) ? form.positionIds : [];
  const mark = form.positionsPrefill || null;
  const untouched = shown.length === 0 || (mark !== null && sameIdSet(shown, mark.ids));
  if (!untouched) return null;
  return { positionIds: ids, positionsPrefill: { userId, ids } };
}
